/**
 * GameViewToggle Component
 * 
 * Segmented control for switching between list, clock, and calendar views.
 * Uses the purple-blue background with cream active state.
 */

import React from 'react';
import { motion } from 'framer-motion';
import styles from './GameViewToggle.module.scss';

export type GameView = 'list' | 'clock' | 'calendar';

interface GameViewToggleProps {
  currentView: GameView;
  onViewChange: (view: GameView) => void;
}

const VIEW_OPTIONS: { id: GameView; label: string }[] = [
  { id: 'clock', label: 'Clock' },
  { id: 'list', label: 'Routine List' },
  { id: 'calendar', label: 'Calendar' },
];

export const GameViewToggle: React.FC<GameViewToggleProps> = ({
  currentView,
  onViewChange,
}) => {
  return (
    <div className={styles.toggle} role="tablist" aria-label="View options">
      {VIEW_OPTIONS.map((option) => {
        const isActive = currentView === option.id;
        
        return (
          <button
            key={option.id}
            className={`${styles.option} ${isActive ? styles.active : ''}`}
            onClick={() => onViewChange(option.id)}
            role="tab"
            aria-selected={isActive}
            aria-controls={`${option.id}-view`}
            type="button"
          >
            {isActive && (
              <motion.div
                className={styles.activeBackground}
                layoutId="activeViewBackground"
                initial={false}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 35,
                }}
              />
            )}
            <span className={styles.label}>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default GameViewToggle;
